import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Crosshair,
  History,
  MapPin,
  RefreshCw,
  Route as RouteIcon,
  Satellite,
} from 'lucide-react';
import {
  POSITION_FRESHNESS_THRESHOLD_MINUTES,
  deviceAssignmentSummary,
  deviceDisplayName,
  deviceToPoint,
} from '@mytag/shared';
import { useDevice, useLocations, useRefreshLocation } from '@/hooks/queries';
import { PageHeader } from '@/components/layout/AppShell';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  EmptyState,
  LoadingState,
} from '@/components/ui/primitives';
import { ErrorState } from '@/components/common/ErrorState';
import {
  BatteryGauge,
  DataRow,
  LocationTypeBadge,
  PositionFreshnessBadge,
  StatusBadge,
} from '@/components/common/indicators';
import { OpenInMapsButton } from '@/components/common/OpenInMapsButton';
import { MapView } from '@/components/map/MapView';
import { formatDateTime, formatMeters, formatRelative, formatSpeed } from '@/lib/format';

export function DeviceDetailsPage(): JSX.Element {
  const { deviceNum } = useParams<{ deviceNum: string }>();
  const { data, isLoading, isError, error, refetch, isFetching } = useDevice(deviceNum);
  // Les 20 derniers points servent de mini-trace sur la fiche.
  const recent = useLocations(deviceNum, { limit: 20 });
  const refresh = useRefreshLocation();

  if (isLoading) return <LoadingState label="Chargement du device…" />;
  if (isError) {
    return (
      <>
        <BackLink />
        <ErrorState error={error} onRetry={() => void refetch()} title="Device introuvable" />
      </>
    );
  }

  const device = data?.device;
  if (!device) return <EmptyState title="Device introuvable" />;

  const current = deviceToPoint(device);
  const refreshedPoint = refresh.data?.location ?? null;
  const trace = recent.data?.points ?? [];
  const mapPoints = current ? [...trace.filter((point) => point.timestamp !== current.timestamp), current] : trace;

  return (
    <>
      <BackLink />
      <PageHeader
        title={deviceDisplayName(device)}
        description={
          deviceAssignmentSummary(device)
            ? `Device ${device.deviceNum} · ${deviceAssignmentSummary(device)}`
            : `Device ${device.deviceNum}`
        }
        actions={
          <>
            <Link to={`/devices/${device.deviceNum}/history`}>
              <Button variant="outline" icon={<RouteIcon className="h-4 w-4" />}>
                Historique
              </Button>
            </Link>
            <Button
              variant="outline"
              icon={<RefreshCw className="h-4 w-4" />}
              loading={isFetching && !refresh.isPending}
              onClick={() => void refetch()}
            >
              Recharger
            </Button>
            <Button
              icon={<Crosshair className="h-4 w-4" />}
              loading={refresh.isPending}
              onClick={() => refresh.mutate(device.deviceNum)}
            >
              Rafraîchir la position
            </Button>
          </>
        }
      />

      {refresh.isPending ? (
        <Alert tone="info" title="Demande de position en cours">
          Le tag doit répondre au serveur Vernal : cela peut prendre quelques secondes.
        </Alert>
      ) : null}
      {refresh.isError ? <ErrorState error={refresh.error} title="Rafraîchissement impossible" /> : null}
      {refresh.isSuccess ? (
        <Alert tone={refreshedPoint ? 'success' : 'warning'} title={refresh.data.message}>
          {refreshedPoint ? (
            <span className="font-mono">
              {refreshedPoint.latitude.toFixed(5)}, {refreshedPoint.longitude.toFixed(5)} ·{' '}
              {formatDateTime(refreshedPoint.timestamp)}
            </span>
          ) : (
            'Aucune position renvoyée pour le moment : réessayer dans quelques instants.'
          )}
        </Alert>
      ) : null}

      {!current && device.lastHistoricalLocation ? (
        <Alert tone="warning" title="Aucune position en temps réel — dernière position de l’historique">
          <p className="flex items-start gap-2">
            <History className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              Le fournisseur ne renvoie actuellement aucune position en direct (getDeviceList) pour
              cet appareil. Dernière position connue via l’historique — <strong>pas une lecture
              temps réel</strong> :{' '}
              <span className="font-mono">
                {device.lastHistoricalLocation.latitude.toFixed(5)},{' '}
                {device.lastHistoricalLocation.longitude.toFixed(5)}
              </span>{' '}
              · {formatDateTime(device.lastHistoricalLocation.timestamp)}
            </span>
          </p>
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex-row items-center justify-between">
            <CardTitle>Position actuelle</CardTitle>
            {current ? <LocationTypeBadge type={current.locationType} /> : null}
          </CardHeader>
          <CardContent>
            {mapPoints.length > 0 ? (
              <MapView points={mapPoints} showRoute={mapPoints.length > 1} height="420px" />
            ) : (
              <EmptyState
                icon={<MapPin className="h-5 w-5" />}
                title="Aucune position connue"
                description="Lancer « Rafraîchir la position » pour interroger le tag."
                action={
                  <Button
                    loading={refresh.isPending}
                    icon={<Crosshair className="h-4 w-4" />}
                    onClick={() => refresh.mutate(device.deviceNum)}
                  >
                    Rafraîchir la position
                  </Button>
                }
              />
            )}
          </CardContent>
          <CardFooter>
            <OpenInMapsButton point={current} label={deviceDisplayName(device)} />
          </CardFooter>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>État</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                {device.known ? <Badge tone="info">Device de test</Badge> : null}
              </div>
              <BatteryGauge level={device.battery} showBar className="w-full" />
              <dl className="divide-y divide-border">
                <DataRow label="Connexion fournisseur" value={<StatusBadge status={device.status} />} />
                <DataRow
                  label="Dernière position reçue"
                  value={`${formatDateTime(device.lastUpdate)} (${formatRelative(device.lastUpdate)})`}
                />
                <DataRow
                  label={`Fraîcheur (seuil ${POSITION_FRESHNESS_THRESHOLD_MINUTES} min)`}
                  value={<PositionFreshnessBadge lastUpdate={device.lastUpdate} />}
                />
                <DataRow
                  label="Latitude"
                  value={device.latitude !== null ? device.latitude.toFixed(6) : '—'}
                  mono
                />
                <DataRow
                  label="Longitude"
                  value={device.longitude !== null ? device.longitude.toFixed(6) : '—'}
                  mono
                />
                <DataRow label="Précision" value={formatMeters(device.accuracy)} />
                <DataRow label="Vitesse" value={formatSpeed(current?.speed ?? null)} />
              </dl>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Satellite className="h-4 w-4 text-muted-foreground" aria-hidden />
                Informations device
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-border">
                <DataRow label="deviceNum" value={device.deviceNum} mono />
                <DataRow label="Modèle" value={device.model ?? '—'} />
                <DataRow label="IMEI" value={device.imei ?? '—'} mono />
                <DataRow label="ICCID" value={device.iccid ?? '—'} mono />
                <DataRow
                  label="Source de position"
                  value={<LocationTypeBadge type={device.locationType} />}
                />
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>

      {recent.isError ? (
        <ErrorState error={recent.error} title="Trace récente indisponible" onRetry={() => void recent.refetch()} />
      ) : null}
    </>
  );
}

function BackLink(): JSX.Element {
  return (
    <Link
      to="/devices"
      className="focus-ring inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
      Retour aux devices
    </Link>
  );
}
